import forge from 'node-forge';
import fs from 'fs';
import { DateTime, Interval } from 'luxon';
import publicKeyHandler from './publicKeyHandler';
import {
  Agencies,
  HelpTypes,
  MiscType,
  ProfessionalReplacementTypes,
  ReplacementHelpTypesMap,
  TypeOfUser,
} from './Constants';
import {
  IAdminCalendar,
  IAdminEventsList,
  ILongContractAttributes,
  ISyncRecordRequest,
} from '../models';

export const convertBase64ToImage = (base64: string, contentType: string) => {
  return `data:${contentType};base64,${base64}`;
};

export const encodeImageUrl = (url?: string | null) =>
  url?.replace(/ /g, '%20') ?? '';

export const ArrayToDD = (data: Array<any>) =>
  data?.map((item) => ({
    key: item,
    label: item,
  }));

export const dropdownMapper = <T, key extends keyof T>(
  data: Array<T>,
  id: key,
  desc: key,
) =>
  data?.map((item) => ({
    key: item[id],
    label: item[desc],
  }));

export const dropdownMinMaxMapper = <T, key extends keyof T>(
  data: Array<T>,
  desc: key,
) =>
  data?.map((item) => ({
    key: { min: item?.min, max: item?.max },
    label: item[desc],
  }));

export const radioButtonMapper = <T, key extends keyof T>(
  data: Array<T>,
  id: key,
  desc: key,
) =>
  data?.map((item) => ({
    value: item[id],
    label: item[desc],
  }));

export const getPasswordStrength = (password: string) => {
  const rates = {
    1: {
      label: 1,
      regex: '',
      color: '#D50000',
    },
    2: {
      label: 2,
      regex: '',
      color: '#FF6F00',
    },
    3: {
      label: 3,
      regex: '',
      color: '#FFD600',
    },
    4: {
      label: 4,
      regex: '',
      color: '#00C853',
    },
  };

  const { length } = password;
  const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
  const specialCharCount = (password.match(/[^a-zA-Z0-9]/g) || []).length;
  const digitCount = (password.match(/[0-9]/g) || []).length;

  switch (true) {
    case length >= 8 &&
      uppercaseCount >= 3 &&
      specialCharCount >= 3 &&
      digitCount >= 3:
      return { level: 4, bgColor: rates[4]?.color, rates: rates };
    case length >= 8 &&
      uppercaseCount >= 2 &&
      specialCharCount >= 2 &&
      digitCount >= 2:
      return { level: 3, bgColor: rates[3]?.color, rates: rates };
    case length >= 8 &&
      uppercaseCount >= 1 &&
      specialCharCount >= 1 &&
      digitCount >= 1:
      return { level: 2, bgColor: rates[2]?.color, rates: rates };
    case length === 0:
      return { level: 0, bgColor: '#fffff', rates: rates };
    default:
      return { level: 1, bgColor: rates[1]?.color, rates: rates };
  }
};

export const encryptPassword = (password: string) => {
  const pkstr =
    '-----BEGIN PUBLIC KEY-----' +
    publicKeyHandler.getKey() +
    '-----END PUBLIC KEY-----';
  const pk = forge.pki.publicKeyFromPem(pkstr);
  return forge.util.encode64(
    pk.encrypt(password, 'RSA-OAEP', {
      md: forge.md.sha1.create(),
      mgf1: {
        md: forge.md.sha1.create(),
      },
    }),
  );
};

export const uniqueHash = () => {
  var randomBytes = forge.random.getBytesSync(32);
  const sha2 = forge.md.sha256.create();
  sha2.update(randomBytes);
  return sha2.digest().toHex();
};

export const isLessThanFifty = (level: number) => level * 25 < 50;
export const stub = () => {};

/*
 @params:
 name - pharmacy name / pharmacist name

 returns:
 name based on user type
*/
export const extractNameFromChat = (name: string, userType: TypeOfUser) => {
  const isPharmacy = userType === TypeOfUser.pharmacy;
  const onlyOneName = 1;

  const [pharmacyName, pharmacistName] = name?.split?.('/') ?? [];
  if (name?.split?.('/')?.length === onlyOneName) return name;
  return isPharmacy ? pharmacistName?.trim() : pharmacyName?.trim();
};

const messageNavigationKey = 'navigation';

const parseMessageData = (data: string): unknown => {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

// DESTINATION A BROADCAST BINDS TO A CHAT MESSAGE, SENT AS JSON IN ITS DATA FIELD
export const getMessageNavigationUrl = (data: unknown): string | null => {
  if (typeof data !== 'string' || !data) {
    return null;
  }

  const parsedData = parseMessageData(data);

  if (typeof parsedData !== 'object' || parsedData === null) {
    return null;
  }

  const navigation = (parsedData as Record<string, unknown>)[
    messageNavigationKey
  ];

  if (typeof navigation !== 'string' || !navigation) {
    return null;
  }

  return navigation;
};

export const handleWorkshiftToggles = (
  toggle: boolean,
  value: string | number | null | Array<string>,
  fallback: any,
) => {
  if (toggle) return value;
  return fallback;
};

export const getHoursDiff = (date1: string, date2: string) => {
  const start = DateTime.fromISO(date1);
  const end = DateTime.fromISO(date2);
  const diff = start.diff(end, ['years', 'months', 'days', 'hours']);

  return diff.toObject()?.hours;
};

const isArrayEqual = (x: Array<string>, y: Array<string>): boolean => {
  if (x.length !== y.length) {
    return false;
  }

  const setX = new Set(x.map((item) => JSON.stringify(item)));
  return y.every((item) => {
    let res = setX.has(JSON.stringify(item));
    return res;
  });
};

export const deepEqual = (a: any, b: any): boolean => {
  let result = true;
  if (a === b) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return isArrayEqual(a, b);
  }

  if (Object.values(a).length !== Object.values(b).length) {
    return false;
  }

  for (const keyA of Object.keys(a)) {
    if (b[keyA] === 'undefined') {
      return false;
    }

    if (
      typeof a[keyA] === 'object' &&
      typeof b[keyA] === 'object' &&
      !deepEqual(a[keyA], b[keyA])
    ) {
      return false;
    }

    if (`${a[keyA]}` !== `${b[keyA]}`) {
      return false;
    }
  }
  return result;
};

export const idOrSyncParams = (
  params: ISyncRecordRequest | string,
): ISyncRecordRequest => {
  if (typeof params === 'string') {
    return { id: params };
  } else {
    return params;
  }
};

export const mergeContractAttributes = (
  interval: Array<ILongContractAttributes>,
  savedInterval: Array<ILongContractAttributes>,
  addAttributes?: Record<string, any>,
) => {
  const mergedArray = [];
  const onlyUniqueIntervals = savedInterval.filter(
    (obj1, i, arr) =>
      arr.findIndex(
        (obj2) =>
          obj2.startDate.split('T')?.[0] === obj1.startDate.split('T')?.[0],
      ) === i,
  );

  mergedArray.push(...onlyUniqueIntervals);

  const uniqueDates = new Set(
    savedInterval.map((item) => item?.startDate?.split?.('T')?.[0]),
  );

  interval.forEach((item) => {
    const date = item?.startDate?.split?.('T')?.[0];

    if (!uniqueDates.has(date)) {
      uniqueDates.add(date);
      mergedArray.push({ ...item, ...addAttributes });
    }
  });

  mergedArray.sort((a, b) =>
    a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0,
  );

  return mergedArray;
};

export const weekdayWeekendCountIterate = (
  shiftContractAttributes: Array<ILongContractAttributes>,
) => {
  let weekdays = 0;
  let weekends = 0;

  for (const item of shiftContractAttributes) {
    if (item.dayOff) continue;

    const date = DateTime.fromISO(item.startDate);

    if (date.weekday === 6 || date.weekday === 7) {
      weekends++;
    } else {
      weekdays++;
    }
  }

  return { weekdays, weekends };
};

export const weekdayWeekendCount = (
  startDate: DateTime | null,
  endDate: DateTime | null,
) => {
  if (startDate && endDate) {
    const s = startDate.startOf('day');
    const e = endDate.startOf('day');

    const days = Math.floor(e?.diff(s, 'days').days ?? 0) + 1; // REMOVE ONE IF YOU WANT TO MAKE RANGE EXCLUSIVE OF END DATE

    const weeks = Math.floor(days / 7);
    let weekdays = weeks * 5;
    let weekends = weeks * 2;

    const remainingDays = days % 7;

    for (let i = 0; i < remainingDays; i++) {
      const d = ((s.weekday + i - 1) % 7) + 1;
      if (d >= 6) weekends++;
      else weekdays++;
    }

    return { weekdays, weekends };
  }

  return { weekdays: 0, weekends: 0 };
};

export const getDaysDistribution = (
  longTermAttributes: Array<ILongContractAttributes>,
) => {
  return longTermAttributes.reduce(
    (acc, curr) => {
      const { startDate, endDate, dayOff } = curr ?? {};
      if (dayOff) {
        return acc;
      }

      const start = DateTime.fromISO(startDate, { setZone: true });
      const end = DateTime.fromISO(endDate, { setZone: true });

      const { hours } = end.diff(start, 'hours');
      if (start.weekday >= 6) {
        acc.weekends++;
        acc.weekendHours += hours;
      } else {
        acc.weekdays++;
        acc.weekdayHours += hours;
      }

      return acc;
    },
    {
      weekdayHours: 0,
      weekendHours: 0,
      weekdays: 0,
      weekends: 0,
    },
  );
};

export const goToFallback = <T,>(val: T, fallback: any) => {
  if (val) return val;
  return fallback;
};

export const filterByLongTerm = <
  T extends { longTermShiftContractId?: string },
>(
  event: T,
) => !!event?.longTermShiftContractId;

export const filterByReplacementType =
  (replacementType: HelpTypes | MiscType) =>
  <T extends { replacementType?: ProfessionalReplacementTypes }>(event: T) => {
    if (replacementType === MiscType.All) return true;
    return (
      event.replacementType ===
      ReplacementHelpTypesMap[
        replacementType as keyof typeof ReplacementHelpTypesMap
      ]
    );
  };

export const filterByShortTerm = <
  T extends { longTermShiftContractId?: string },
>(
  event: T,
) => !event.longTermShiftContractId;

const mimeMap = {
  png: 'image/png',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  docx: 'application/octet-stream',
  doc: 'application/octet-stream',
};

export const determineMime = (fileName: string) => {
  const re = /(?:\.([^.]+))?$/;

  const extension = re?.exec?.(fileName)?.[1];

  return mimeMap[extension as keyof typeof mimeMap];
};

export const filterByPosted =
  (filterPostedBy: Agencies | MiscType) => (events: any) => {
    if (filterPostedBy === Agencies.PharmaPlan) {
      return (events as IAdminCalendar)?.isAutoSyncRecord;
    }

    if (filterPostedBy === Agencies.PharmaGap) {
      return !(events as IAdminCalendar)?.isAutoSyncRecord;
    }

    return true;
  };
